import { ChangeEvent, useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { cn } from "../ClassName";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MASKED_DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** YYYY-MM-DD -> MM/DD/YYYY; "" for anything that isn't a stored date. */
export const isoToMaskedDate = (iso: string | null | undefined): string => {
  const match = ISO_DATE_RE.exec((iso ?? "").trim());
  return match ? `${match[2]}/${match[3]}/${match[1]}` : "";
};

/**
 * Progressively formats whatever the user typed as MM/DD/YYYY.
 *
 * Only digits survive, so the separators are re-derived on every keystroke and
 * a backspace over a "/" deletes the digit before it instead of re-inserting
 * the slash ("12/" -> "12").
 *
 * A whole YYYY-MM-DD arriving at once is reformatted instead of stripped: that
 * is what a paste from a chart — or automation filling the field — looks like,
 * and dropping the dashes would mask "1990-01-01" as "19/90/0101". Typing can
 * never produce that shape, since only digits and our own slashes survive.
 */
export const maskDateText = (raw: string): string => {
  const pastedIso = isoToMaskedDate(raw);
  if (pastedIso) return pastedIso;
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/**
 * Parses a complete MM/DD/YYYY string into the YYYY-MM-DD value the order form
 * stores. Returns null for anything incomplete or impossible (02/30/1990,
 * 13/01/1990), so a half-typed date never reads as a real one.
 */
export const maskedDateToIso = (text: string): string | null => {
  const match = MASKED_DATE_RE.exec(text.trim());
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const month = Number(mm);
  const day = Number(dd);
  const year = Number(yyyy);
  // Two-digit years are ambiguous and the Date constructor maps them into the
  // 1900s, so require a real four-digit year rather than silently guessing.
  if (year < 1000) return null;
  const date = new Date(year, month - 1, day);
  // Date rolls overflow forward (Feb 30 -> Mar 2), so round-trip the parts.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return `${yyyy}-${mm}-${dd}`;
};

interface MaskedDateInputProps {
  id?: string;
  /** Stored value, YYYY-MM-DD (or "" when unset). */
  value: string;
  /** Called with a YYYY-MM-DD string, or "" while the date is incomplete. */
  onChange: (isoDate: string) => void;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  ariaLabel?: string;
  /** Defaults to "off"; a login asks for "bday". */
  autoComplete?: string;
  /**
   * Replaces the default box (border, padding, height, hover, focus, invalid)
   * for a host with its own field design: merging cannot undo the default's
   * focus variants. Style the invalid state with `aria-[invalid=true]:`.
   */
  inputClassName?: string;
  /** Merged onto the calendar button. */
  iconClassName?: string;
  /** The calendar button's icon; defaults to Solid `calendar-days`. */
  icon?: IconDefinition;
  /** Merged onto the "Enter a date as MM/DD/YYYY" line. */
  invalidMessageClassName?: string;
  /**
   * Told whether the field holds text that is not a whole, real date, while
   * `value` reads "". A host that treats "" as "no date" can refuse instead.
   */
  onIncompleteChange?: (incomplete: boolean) => void;
  /** Also flag a partial date once the field is left, until typing resumes. */
  flagIncompleteOnBlur?: boolean;
  /** Flag a partial date now, e.g. once the host refused a submit over it. */
  flagIncomplete?: boolean;
}

const DEFAULT_BOX_CLASS =
  "tw-border-box tw-h-[36px] tw-w-full tw-cursor-text tw-rounded-lg tw-border-[1px] tw-border-solid tw-border-gray-5 tw-p-[8px] tw-pr-[32px] placeholder:tw-overflow-visible placeholder:tw-text-gray-5 hover:tw-border-gray-0 focus:tw-border-[2px] focus:tw-border-blue-2 focus:tw-p-[7px] focus:tw-pr-[31px] focus:tw-shadow-[0px_0px_4px_0px_rgba(0,133,222,0.50)] focus:tw-outline-0 1040:tw-px-[4px] focus:1040:tw-px-[3px]";

/**
 * A date field that can actually be typed into on a tablet.
 *
 * A native `<input type="date">` is replaced by mobile browsers with a
 * picker-only widget that takes no keystrokes, which is why clinicians on
 * tablets could not enter a date of birth (VM-11262). This renders a text
 * input with an MM/DD/YYYY mask and `inputMode="numeric"` — so the device
 * shows a number pad — and parses to YYYY-MM-DD before calling `onChange`, so
 * callers keep the same value shape they had with the native control.
 *
 * The native picker is kept as a secondary affordance behind the calendar
 * button for anyone who prefers it.
 */
export function MaskedDateInput(props: MaskedDateInputProps) {
  const [text, setText] = useState<string>(() => isoToMaskedDate(props.value));
  const pickerRef = useRef<HTMLInputElement>(null);
  const [left, setLeft] = useState(false);

  // Re-sync the visible text when the stored value changes from outside the
  // component (patient autofill, "clear form", loading an existing order).
  // Skipped while the text already round-trips to the stored value, so typing
  // is never clobbered mid-entry.
  useEffect(() => {
    const parsed = maskedDateToIso(text);
    if (parsed !== (props.value || null)) {
      setText(isoToMaskedDate(props.value));
    }
  }, [props.value]);

  const digitCount = text.replace(/\D/g, "").length;
  const parsedText = maskedDateToIso(text);
  // Only complain once all eight digits are in; a partial date is just
  // unfinished, not wrong, unless the host opts in to flagging it.
  const incomplete = text !== "" && parsedText === null;
  const showInvalid =
    (digitCount === 8 && parsedText === null) ||
    (!!props.flagIncompleteOnBlur && left && incomplete) ||
    (!!props.flagIncomplete && incomplete);

  useEffect(() => {
    props.onIncompleteChange?.(incomplete);
  }, [incomplete]);

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const masked = maskDateText(e.target.value);
    setText(masked);
    setLeft(false);
    const iso = maskedDateToIso(masked);
    // Emit "" rather than holding the previous date: an incomplete field must
    // not leave a stale value behind in form state.
    if (iso !== (props.value || null)) {
      props.onChange(iso ?? "");
    }
  };

  const handlePickerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const iso = e.target.value;
    setText(isoToMaskedDate(iso));
    props.onChange(iso);
  };

  const openPicker = () => {
    const el = pickerRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        // Browsers throw when showPicker is called without user activation or
        // when unsupported; fall through to focusing the input instead.
      }
    }
    el.focus();
    el.click();
  };

  return (
    <span className="tw-block">
      {/* Its own box, so the button centers on the input, not on the input
          plus the invalid line. */}
      <span className="tw-relative tw-block">
        <input
          id={props.id}
          type="text"
          inputMode="numeric"
          autoComplete={props.autoComplete ?? "off"}
          placeholder="MM/DD/YYYY"
          maxLength={10}
          disabled={props.disabled}
          aria-label={props.ariaLabel}
          aria-invalid={showInvalid || undefined}
          className={cn(
            props.inputClassName ?? [
              DEFAULT_BOX_CLASS,
              showInvalid && "tw-border-red-1 focus:tw-border-red-1",
            ],
            props.className,
            props.disabled && "hover:tw-border-gray-5",
          )}
          value={text}
          onChange={handleTextChange}
          onBlur={() => setLeft(true)}
          data-testid={props.dataTestId}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          disabled={props.disabled}
          onClick={openPicker}
          className={cn(
            "tw-absolute tw-inset-y-0 tw-right-[8px] tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-gray-4",
            props.iconClassName,
          )}
          data-testid={
            props.dataTestId ? `${props.dataTestId}-picker` : undefined
          }
        >
          <FontAwesomeIcon icon={props.icon ?? faCalendarDays} />
        </button>
        {/* The native control, kept only as the picker the button opens. It stays
          rendered (showPicker throws on a display:none input) but is invisible
          and untabbable. */}
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          min={props.min}
          max={props.max}
          disabled={props.disabled}
          value={props.value || ""}
          onChange={handlePickerChange}
          className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-[8px] tw-size-px tw-border-0 tw-p-0 tw-opacity-0"
          data-testid={
            props.dataTestId ? `${props.dataTestId}-native` : undefined
          }
        />
      </span>
      {showInvalid && (
        <span
          className={cn(
            "tw-mt-[2px] tw-block tw-text-[12px] tw-text-red-1",
            props.invalidMessageClassName,
          )}
        >
          Enter a date as MM/DD/YYYY
        </span>
      )}
    </span>
  );
}
